import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { useTranslations } from '@core/js/hooks/i18n';
import { useUpdateEffect } from '@core/js/hooks/useUpdateEffect';
import { useUserSelector } from '@core/js/store/hooks';
import { createScriptElement } from '@core/js/utils/DOM.util';

import { useCustomUserSelector } from '@interlayer/js/custom/stores/user';
import { setIsAdaLoaded } from '@interlayer/js/custom/stores/user/actions';

import { startAdaEmbed, stopAdaEmbed, toggleAdaChat, triggerAdaCampaign } from './helpers';

let adaEventKeysQueue: string[] = [];
let isAdaLoadedLocal = false;

export const runAdaCommand = (command: string) => {
    if (isAdaLoadedLocal) {
        triggerAdaCampaign(command);
    } else {
        adaEventKeysQueue.push(command);
    }
};

export const useAppendAdaScript = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const t = useTranslations('playtech.mobile.system.adaChatConfig.');
    const adaScript = useRef<null | HTMLScriptElement>(null);

    const toggleIsAdaChatLoaded = useCallback((isLoaded: boolean) => {
        isAdaLoadedLocal = isLoaded;
        dispatch(setIsAdaLoaded(isLoaded));

        if (!isLoaded && adaScript.current) {
            adaEventKeysQueue = [];
            stopAdaEmbed().then(() => {
                document.body.removeChild(adaScript.current!);
                adaScript.current = null;
            });
        }
    }, [dispatch]);

    useEffect(() => {
        const adaSrc = t('src');

        if (!isLoggedIn || !adaSrc || isAdaLoadedLocal || adaScript.current) {
            return;
        }

        // @ts-expect-error: remove after createScriptElement become ts file
        adaScript.current = createScriptElement({
            src: adaSrc,
            attrs: {
                async: true,
                id: t('id', '__ada'),
                'data-handle': t('handle', 'sun'),
                'data-cluster': t('cluster', 'eu'),
                'data-lazy': ''
            }
        });

        adaScript.current!.onload = () => {
            startAdaEmbed().then(() => {
                toggleIsAdaChatLoaded(true);

                adaEventKeysQueue.forEach((command) => {
                    runAdaCommand(command);
                });
            });
        };

        document.body.appendChild(adaScript.current!);
    }, [isLoggedIn, t, toggleIsAdaChatLoaded]);

    useUpdateEffect(() => {
        if (isLoggedIn) {
            return;
        }

        toggleIsAdaChatLoaded(false);
    }, [isLoggedIn]);
};

export const useAdaChat = () => {
    const isAdaLoaded = useCustomUserSelector(prop('isAdaLoaded'));

    return {
        isAdaLoaded,
        toggleAdaChat
    };
};
